<template>
  <div class="px-6">
    <v-divider class="d-xl-none"></v-divider>
    <v-simple-table>
      <template v-slot:default>
        <tbody class="black--text">
          <tr>
            <td class="pl-9 font-16 font-weight-bold">
              Insurance premium (ex IPT)
            </td>
            <td class="text-right font-weight-bold pr-10 font-16">
              {{
                price.Values.HouseholdInsurancePremiumExcludingIPT
                  | formatCurrency
              }}
            </td>
          </tr>
          <tr v-if="hasHomeEmergency">
            <td class="pl-9 font-16 font-weight-bold" v-if="isLandlords">
              Landlords emergency cover (ex IPT)
            </td>
            <td class="pl-9 font-16 font-weight-bold" v-else>
              Home emergency cover (ex IPT)
            </td>
            <td class="text-right font-weight-bold pr-10 font-16">
              {{
                price.Values.HomeEmergencyPremiumExcludingIPT | formatCurrency
              }}
            </td>
          </tr>
          <tr v-if="hasLegalExpenses">
            <td class="pl-9 font-16 font-weight-bold">
              Legal expenses cover (ex IPT)
            </td>
            <td class="text-right font-weight-bold pr-10 font-16">
              {{
                price.Values.LegalExpensesPremiumExcludingIPT | formatCurrency
              }}
            </td>
          </tr>
          <tr v-if="hasRentProtection">
            <td class="pl-9 font-16 font-weight-bold">
              Rent protection cover (ex IPT)
            </td>
            <td class="text-right font-weight-bold pr-10 font-16">
              {{
                price.Values.RentProtectionPremiumExcludingIPT | formatCurrency
              }}
            </td>
          </tr>
          <tr>
            <td class="pl-9 font-16 font-weight-bold">
              Insurance premium tax*
            </td>
            <td class="text-right font-weight-bold pr-10 font-16">
              {{ price.Values.InsurancePremiumTax | formatCurrency }}
            </td>
          </tr>
          <tr>
            <td class="pl-9 font-16 font-weight-bold">Administration charge</td>
            <td class="text-right font-weight-bold pr-10 font-16">
              {{ price.Values.AdministrationCharge | formatCurrency }}
            </td>
          </tr>
          <tr v-if="showTotal">
            <td class="pl-9 font-16 font-weight-bold">
              Total premium (inc IPT)
            </td>
            <td class="text-right font-weight-bold pr-10 font-16">
              {{ price.Values.TotalPremiumIncludingIPT | formatCurrency }}
            </td>
          </tr>
          <tr v-if="hasCredit">
            <td class="pl-9 font-16 font-weight-bold">Charge for credit</td>
            <td class="text-right font-weight-bold pr-10 font-16">
              {{ premium.CreditDetails.CreditCharge | formatCurrency }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <div
      style="background-color: #354048"
      class="white--text pa-2 d-flex justify-space-between pl-9 px-10 font-18 font-weight-bold gap-10"
    >
      <span> Total amount payable </span>
      <span>
        {{ price.Values.TotalAmountPayable | formatCurrency }}
      </span>
    </div>
    <div
      v-if="breakdownModel.paymentFrequency !== ANNUAL"
      style="background-color: #106175"
      class="white--text pa-2 d-flex justify-space-between pl-9 px-10 font-18 font-weight-bold gap-10"
    >
      <span> Monthly price (including charge for credit) </span>
      <span>
        {{ premium.CreditDetails.SubsequentPayments | formatCurrency }}
      </span>
    </div>
    <p class="pt-3 pb-4 px-0 font-14">
      *Note: Insurance premium tax is charged at the current rate and may be
      subject to review.
    </p>

    <v-container v-if="hasCredit">
      <h5 class="font-weight-bold mb-4 font-20 black2-color">
        Representative example - {{ durationMonths }} monthly payments
      </h5>
      <v-simple-table>
        <template v-slot:default>
          <tbody class="black--text">
            <tr>
              <td class="pl-9 font-weight-bold font-16">
                Representative APR
                {{
                  premium.CreditDetails.Apr
                    | formatPercent(representativePercentageFormat)
                }}
              </td>
              <td
                class="text-right font-weight-bold pr-10 font-weight-bold font-16"
              >
                {{ price.Values.TotalAmountPayable | formatCurrency }}
              </td>
            </tr>
            <tr>
              <td class="pl-9 font-16 font-weight-bold">
                Cash price / total credit amount
              </td>
              <td class="text-right font-weight-bold pr-10 font-16">
                {{ price.Values.TotalPremiumIncludingIPT | formatCurrency }}
              </td>
            </tr>
            <tr>
              <td class="pl-9 font-16 font-weight-bold">Interest charges</td>
              <td
                class="text-right font-weight-bold pr-10 font-weight-bold font-16"
              >
                {{ premium.CreditDetails.CreditCharge | formatCurrency }}
              </td>
            </tr>
            <tr>
              <td class="pl-9 font-weight-bold font-16">Fixed interest rate</td>
              <td
                class="text-right font-weight-bold pr-10 font-weight-bold font-16"
              >
                {{
                  premium.CreditDetails.FixedInterestRate
                    | formatPercent(percentageFormat)
                }}
              </td>
            </tr>
            <tr>
              <td class="pl-9 font-weight-bold font-16">
                Duration of agreement
              </td>
              <td class="text-right font-weight-bold pr-10 font-16">
                {{ durationMonths }} months
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <v-divider></v-divider>
      <p class="mt-3">
        Payable by one payment of
        {{ premium.CreditDetails.InitialPayment | formatCurrency }} and
        {{ premium.CreditDetails.NumberOfSubsequentPayments }} payments of
        {{ premium.CreditDetails.SubsequentPayments | formatCurrency }}.
      </p>
      <p class="mb-8">
        Credit is provided by Paymentshield Ltd of Southport Business Park,
        Wight Moss Way, Southport, PR8 4HQ
      </p>
    </v-container>
    <h5 class="black2-color font-weight-bold mt-5 font-20">
      Cancelling your policy
    </h5>
    <p class="mb-0 mt-6">
      You have the right to cancel your policy within the 14 day cooling off
      period. After the 14 day cooling off period, you will still have the right
      to cancel your policy, however you will be charged for the cost of your
      cover on a pro rata daily basis. If you have suffered a claim and you wish
      to cancel your policy you will be required to pay for your policy in full.
    </p>
  </div>
</template>

<script>
import { ANNUAL } from "@/constants/common.constants";
import {
  BREAKDOWN_PERCENTAGE_FORMAT,
  PERCENTAGE_FORMAT,
} from "@/constants/currency-format.constants";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { mapGetters } from "vuex";

export default {
  name: "D2CDialogContentPremiumBreakdown",
  data() {
    return {
      ANNUAL,
      percentageFormat: BREAKDOWN_PERCENTAGE_FORMAT,
      representativePercentageFormat: PERCENTAGE_FORMAT,
    };
  },
  props: {
    breakdownModel: [Array, Object],
  },
  components: {},
  methods: {},
  computed: {
    ...mapGetters(QUESTIONS_MODULE, ["isLandlords"]),
    price() {
      return this.breakdownModel.price;
    },
    premium() {
      return this.breakdownModel.premium;
    },
    hasCredit() {
      return !!this.premium.CreditDetails;
    },
    durationMonths() {
      return this.premium.CreditDetails
        ? this.premium.CreditDetails.NumberOfSubsequentPayments + 1
        : null;
    },
    showTotal() {
      return (
        this.price.Values.TotalPremiumIncludingIPT !==
        this.price.Values.TotalAmountPayable
      );
    },
    hasRentProtection() {
      return (
        this.breakdownModel.price.Values.RentProtectionPremiumExcludingIPT > 0
      );
    },
    hasHomeEmergency() {
      return (
        this.breakdownModel.price.Values.HomeEmergencyPremiumExcludingIPT > 0
      );
    },
    hasLegalExpenses() {
      return (
        this.breakdownModel.price.Values.LegalExpensesPremiumExcludingIPT > 0
      );
    },
  },
};
</script>

<style lang="scss" scoped>
tbody {
  tr:hover {
    background-color: transparent !important;
  }
}

tr:nth-of-type(even) {
  background-color: #f5f5f5 !important;
}
</style>
